import { useState } from 'react';
import { useFeedback } from './store';
import { SEVERITIES, TAGS, viewport } from './constants';
import type { Severity, Tag } from './types';

export function FeedbackFab() {
  const { tickets, fbOpen, setFbOpen, dashOpen } = useFeedback();
  // The dashboard covers capture; do not offer a launcher above that overlay.
  if (dashOpen) return null;
  return (
    <button
      className="proto-fab"
      onClick={() => setFbOpen(!fbOpen)}
      aria-label="Open feedback"
      title="Leave feedback (f)"
    >
      <span
        style={{ fontFamily: 'Instrument Serif, serif', fontSize: 26, lineHeight: 1, marginTop: -2 }}
      >
        ✎
      </span>
      {tickets.length > 0 && <span className="badge">{tickets.length}</span>}
    </button>
  );
}

export function CapturePanel() {
  const {
    fbOpen,
    setFbOpen,
    tickets,
    addTicket,
    pushToast,
    setDashOpen,
    setCommentMode,
    route,
    routeLabel,
    screenshots,
    screenshotUrl,
  } = useFeedback();
  const [note, setNote] = useState('');
  const [tag, setTag] = useState<Tag>('comment');
  const [severity, setSeverity] = useState<Severity>('medium');

  if (!fbOpen) return null;

  const submit = () => {
    if (!note.trim()) return;
    const t = addTicket({ kind: 'note', source: 'manual', tag, severity, note: note.trim() });
    setNote('');
    setTag('comment');
    setSeverity('medium');
    setFbOpen(false);
    pushToast({ kind: 'info', text: `Ticket ${t.id} created · ${tickets.length + 1} total`, ttl: 4000 });
  };

  const openDashboard = () => {
    setFbOpen(false);
    setDashOpen(true);
  };

  const describeWithoutArea = () => {
    setFbOpen(false);
    setCommentMode(true);
    window.dispatchEvent(new CustomEvent('fbtk:describe-without-area'));
  };

  return (
    <div className="proto-fb-modal" role="dialog" aria-label="Capture feedback">
      <div className="proto-fb-head">
        <h3>New ticket</h3>
        <span className="hint" style={{ marginLeft: 'auto' }}>
          {tickets.length} total
        </span>
        <button className="x" onClick={() => setFbOpen(false)} aria-label="Close">
          ×
        </button>
      </div>

      <div className="proto-fb-body">
        <div className="proto-fb-meta">
          <span className="tag">{route}</span>
          {routeLabel && routeLabel !== route && <span className="tag">{routeLabel}</span>}
          <span className="tag">{viewport()}</span>
        </div>

        {screenshotUrl && screenshots && (
          <p className="proto-fb-privacy">
            A screenshot of this screen is attached exactly as it appears, including
            any text and figures on it.
          </p>
        )}

        <div>
          <div className="proto-fb-row-label">TYPE</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TAGS.map((t) => (
              <button
                key={t}
                onClick={() => setTag(t)}
                style={{
                  padding: '4px 10px',
                  border: '1px solid ' + (tag === t ? 'var(--p-ink)' : 'var(--p-line)'),
                  background: tag === t ? 'var(--p-ink)' : '#fff',
                  color: tag === t ? '#fff' : 'var(--p-ink-2)',
                  borderRadius: 100,
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textTransform: 'capitalize',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="proto-fb-row-label">SEVERITY</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {SEVERITIES.map((s) => (
              <button
                key={s.id}
                onClick={() => setSeverity(s.id)}
                style={{
                  padding: '4px 10px',
                  border: '1px solid ' + (severity === s.id ? s.dot : 'var(--p-line)'),
                  background:
                    severity === s.id ? `color-mix(in srgb, ${s.dot} 12%, white)` : '#fff',
                  color: severity === s.id ? 'var(--p-ink)' : 'var(--p-ink-2)',
                  borderRadius: 100,
                  fontSize: 11,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot }} />
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <textarea
          className="proto-fb-textarea"
          placeholder="What stood out on this screen? What's confusing? What would you change?"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
          }}
        />
      </div>

      <div className="proto-fb-foot">
        <button className="ghost" onClick={openDashboard}>
          All tickets →
        </button>
        <div className="right">
          <button className="ghost" onClick={describeWithoutArea}>
            Describe without area
          </button>
          <button className="ghost" onClick={() => setFbOpen(false)}>
            Cancel
          </button>
          <button className="submit" onClick={submit} disabled={!note.trim()}>
            Create ticket · ⌘↵
          </button>
        </div>
      </div>
    </div>
  );
}
